/**
 * A side pane's edge facing the canvas, as a window splitter (WAI-ARIA):
 * dragged with the pointer, or focused and moved with the arrow keys, Home and
 * End; a double click puts it back to its default. The arrows move the edge
 * the way they point, so on a right-hand pane ArrowLeft widens it.
 *
 * It reports widths as they change and says when a change is finished, so the
 * pane follows the pointer live and the width is stored once, not per pixel.
 */
import { useRef, type PointerEvent as ReactPointerEvent } from "react";
import type { PaneLimits } from "./preferences";

/** How far one arrow key moves the edge. */
const RESIZE_STEP = 16;

export function clampPaneWidth(width: number, limits: PaneLimits): number {
  return Math.round(Math.max(limits.minimum, Math.min(limits.maximum, width)));
}

export function PaneResizer({
  label,
  width,
  limits,
  edge,
  onResize,
  onDone,
}: {
  /** "Resize palette", "Resize inspector". */
  label: string;
  width: number;
  limits: PaneLimits;
  /** Which side of the pane the handle is on: a left pane's right edge, a right pane's left edge. */
  edge: "right" | "left";
  onResize: (width: number) => void;
  onDone: () => void;
}) {
  const drag = useRef<{ x: number; width: number } | null>(null);
  // Moving the pointer right widens a pane whose edge is on its right.
  const sign = edge === "right" ? 1 : -1;
  const end = (event: ReactPointerEvent) => {
    if (!drag.current) return;
    drag.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    onDone();
  };
  return (
    <div
      className={`ws-resize ws-resize-${edge}`}
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width}
      aria-valuemin={limits.minimum}
      aria-valuemax={limits.maximum}
      tabIndex={0}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        drag.current = { x: event.clientX, width };
        event.currentTarget.setPointerCapture?.(event.pointerId);
      }}
      onPointerMove={(event) => {
        if (drag.current) onResize(clampPaneWidth(drag.current.width + sign * (event.clientX - drag.current.x), limits));
      }}
      onPointerUp={end}
      onPointerCancel={end}
      onDoubleClick={() => {
        onResize(limits.default);
        onDone();
      }}
      onKeyDown={(event) => {
        const next =
          event.key === "ArrowLeft" ? width - sign * RESIZE_STEP
          : event.key === "ArrowRight" ? width + sign * RESIZE_STEP
          : event.key === "Home" ? limits.minimum
          : event.key === "End" ? limits.maximum
          : null;
        if (next === null) return;
        event.preventDefault();
        onResize(clampPaneWidth(next, limits));
        onDone();
      }}
    />
  );
}
